"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import {
  Modal,
  ModalContent,
  ModalBody,
  Button,
  useDisclosure,
} from "@heroui/react";
import { ChevronLeft, ChevronRight, X, Image as ImageIcon } from "lucide-react";
import { EventMedia } from "@/types/event.types";

interface EventGalleryProps {
  eventName: string;
  mainImage: string;
  eventMedia: EventMedia[];
  className?: string;
}

export default function EventGallery({
  eventName,
  eventMedia,
  className = "",
}: EventGalleryProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const sliderRef = useRef<HTMLDivElement>(null);
  const displayImages = [
    ...new Set(
      eventMedia
        .filter(
          (media) =>
            media.type === "image" && media.url && !failedImages.has(media.url),
        )
        .map((media) => media.url),
    ),
  ];
  const currentIndex = Math.max(0, displayImages.indexOf(currentImage || ""));
  const handleImageError = (url: string) => {
    setFailedImages((previous) => new Set([...previous, url]));
  };
  const openGalleryModal = (url: string) => {
    setCurrentImage(url);
    onOpen();
  };
  const moveImage = (direction: number) => {
    setCurrentImage(
      displayImages[
        (currentIndex + direction + displayImages.length) % displayImages.length
      ],
    );
  };
  const scrollSlider = (direction: number) => {
    const slider = sliderRef.current;
    slider?.scrollBy({
      left: direction * slider.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  if (!displayImages.length) return null;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-semibold">Event Gallery</h2>
        </div>
        {displayImages.length > 1 && (
          <div className="flex gap-2">
            <Button
              isIconOnly
              aria-label="Scroll gallery left"
              variant="flat"
              className="bg-surface-raised/70 text-ink"
              onPress={() => scrollSlider(-1)}
            >
              <ChevronLeft size={20} />
            </Button>
            <Button
              isIconOnly
              aria-label="Scroll gallery right"
              variant="flat"
              className="bg-surface-raised/70 text-ink"
              onPress={() => scrollSlider(1)}
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        )}
      </div>
      <div
        className="relative w-full overflow-x-auto hide-scrollbar pb-4"
        ref={sliderRef}
      >
        <div className="flex space-x-4 w-max">
          {displayImages.map((url, index) => (
            <button
              key={url}
              type="button"
              aria-label={`View ${eventName} photo ${index + 1}`}
              className="w-[280px] sm:w-[320px] md:w-[380px] lg:w-[420px] flex-shrink-0 aspect-[5/3] bg-surface/50 rounded-lg overflow-hidden cursor-pointer group relative focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => openGalleryModal(url)}
            >
              <Image
                src={url}
                alt={`${eventName} photo ${index + 1}`}
                fill
                sizes="(max-width: 640px) 280px, (max-width: 768px) 320px, (max-width: 1024px) 380px, 420px"
                className="object-cover group-hover:scale-110 transition-transform duration-500 ease-in-out motion-reduce:transition-none"
                onError={() => handleImageError(url)}
              />
              <span className="absolute inset-0 bg-overlay/0 group-hover:bg-overlay/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">
                <span className="theme-media bg-canvas/60 px-4 py-2 rounded-md text-ink text-sm font-medium">
                  View larger
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        classNames={{
          backdrop: "bg-overlay/95",
          base: "theme-media bg-transparent shadow-none",
        }}
        hideCloseButton
      >
        <ModalContent>
          {() => (
            <ModalBody className="p-0 relative">
              <div className="absolute top-0 left-0 right-0 z-50 flex justify-between items-center p-4 bg-gradient-to-b from-media/70 to-transparent">
                <div className="text-ink text-lg md:text-xl font-medium">
                  {eventName}
                </div>
                <Button
                  isIconOnly
                  aria-label="Close gallery"
                  variant="light"
                  className="bg-canvas/50 text-ink"
                  onPress={onClose}
                >
                  <X size={24} />
                </Button>
              </div>
              <div className="relative w-full h-[90vh] flex items-center justify-center">
                <Image
                  src={displayImages[currentIndex]}
                  alt={`${eventName} photo ${currentIndex + 1}`}
                  fill
                  sizes="100vw"
                  className="object-contain"
                  onError={() => handleImageError(displayImages[currentIndex])}
                  priority
                />
                {displayImages.length > 1 && (
                  <>
                    <Button
                      isIconOnly
                      aria-label="Previous photo"
                      variant="light"
                      className="absolute left-4 z-50 bg-canvas/50 text-ink h-12 w-12"
                      onPress={() => moveImage(-1)}
                    >
                      <ChevronLeft size={30} />
                    </Button>
                    <Button
                      isIconOnly
                      aria-label="Next photo"
                      variant="light"
                      className="absolute right-4 z-50 bg-canvas/50 text-ink h-12 w-12"
                      onPress={() => moveImage(1)}
                    >
                      <ChevronRight size={30} />
                    </Button>
                  </>
                )}
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <span className="inline-block bg-canvas/60 px-4 py-2 rounded-full text-ink">
                    {currentIndex + 1} / {displayImages.length}
                  </span>
                </div>
              </div>
            </ModalBody>
          )}
        </ModalContent>
      </Modal>
      <style jsx global>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
